import { onSessionReset } from "./session";

type Outcome = { state: string; retryAt?: number };
interface Entry { promise: Promise<Outcome | null>; retryAt: number; running: boolean }

/** Request lifetime is independent of a mounted article or a prepared body. */
export class BodyRequests {
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  running(key: string) { return this.entries.get(key)?.running ?? false; }
  private emit() { this.listeners.forEach((listener) => listener()); }
  constructor(private readonly now = Date.now, private readonly capacity = 256) {}
  clear() { this.entries.clear(); this.emit(); }
  run(key: string, request: () => Promise<Outcome>): Promise<Outcome | null> {
    const prior = this.entries.get(key);
    if (prior && (prior.running || this.now() < prior.retryAt)) return prior.promise;
    const entry: Entry = { promise: Promise.resolve(null), retryAt: Infinity, running: true };
    const attempt = async () => request();
    entry.promise = attempt().then((outcome) => {
      entry.retryAt = outcome.retryAt ?? Infinity;
      return outcome;
    }, () => {
      // Transport failure has no authoritative server cooldown. A later opening retries.
      entry.retryAt = 0;
      return null;
    }).finally(() => { entry.running = false; this.trim(); this.emit(); });
    this.entries.delete(key); this.entries.set(key, entry); this.trim(); this.emit();
    return entry.promise;
  }
  private trim() {
    for (const [key, entry] of this.entries) {
      if (this.entries.size <= this.capacity) break;
      if (!entry.running) this.entries.delete(key);
    }
  }
}

export const bodyRequests = new BodyRequests();
onSessionReset(() => bodyRequests.clear());
